import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import ComparisonTable from "@/components/ComparisonTable";
import ProductCard from "@/components/ProductCard";
import EmailCapture from "@/components/EmailCapture";
import SEOHead from "@/components/SEOHead";
import TableOfContents from "@/components/TableOfContents";
import { ArrowRight, ChevronRight } from "lucide-react";

const TOC = [
  { id: "comparison", label: "Van Life Panel Comparison" },
  { id: "reviews", label: "Recommended Van Life Panels" },
  { id: "flexible-vs-rigid", label: "Flexible vs Rigid for Vans" },
  { id: "sizing", label: "How Much Solar for Van Life" },
  { id: "faq", label: "FAQ" },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Best Solar Panels for Van Life (2026) — Flexible, Rigid & Portable",
  "description": "Best solar panels for van life builds. Flexible vs rigid panels for Sprinter, Transit, and Promaster roofs. Sizing guide and product recommendations.",
  "url": "https://highestefficiencysolarcell.com/solar-panels-for-van-life",
  "dateModified": "2026-07-01",
  "author": { "@type": "Organization", "name": "HighestEfficiencySolarCell.com" },
};

export default function SolarPanelsForVanLife() {
  const { data: products = [] } = useQuery({
    queryKey: ["solar-products"],
    queryFn: () => base44.entities.SolarProduct.list("sort_order", 50),
  });

  const vanProducts = products.filter(
    (p) =>
      p.form_factor === "Flexible" ||
      p.form_factor === "Portable" ||
      p.form_factor === "Foldable" ||
      p.best_use_case?.toLowerCase().includes("van") ||
      p.best_use_case?.toLowerCase().includes("curved")
  );

  const displayProducts = vanProducts.length > 0 ? vanProducts : products.slice(0, 6);

  return (
    <>
      <SEOHead
        title="Best Solar Panels for Van Life 2026 | Flexible, Rigid & Portable Picks"
        description="Best solar panels for van life builds — Sprinter, Transit, and Promaster. Flexible vs rigid panels, sizing guide, and product recommendations. Updated 2026 buyer's guide."
        canonical="https://highestefficiencysolarcell.com/solar-panels-for-van-life"
        schema={SCHEMA}
      />
      <div className="pb-28 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-foreground">Solar Panels for Van Life</span>
          </nav>

          <div className="max-w-4xl mb-2">
            <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">
              Best Solar Panels for Van Life: Flexible, Rigid, and Portable Options
            </h1>
            <p className="text-lg text-muted-foreground mb-6 max-w-3xl">
              Van life solar setups have unique constraints: limited roof space, curved Sprinter and Transit
              roofs, weight sensitivity, and the need for quiet off-grid power. This guide covers the best solar
              panels for van builds — whether you want a low-profile flexible array, efficient rigid panels, or a
              portable supplemental panel.
            </p>

            <AffiliateDisclosure />
            <TableOfContents items={TOC} />
          </div>

          <section id="comparison" className="mb-8">
            <h2 className="font-heading font-bold text-3xl mb-4">Van Life Solar Panel Comparison</h2>
          </section>

          {displayProducts.length > 0 && (
            <>
              <ComparisonTable products={displayProducts} />
              <div className="mt-16" id="reviews">
                <h2 className="font-heading font-bold text-3xl mb-8">Recommended Van Life Solar Panels</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {displayProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="mt-16 max-w-3xl" id="flexible-vs-rigid">
            <h2 className="font-heading font-bold text-2xl mb-6">Flexible vs Rigid Panels for Van Life</h2>
            <div className="space-y-5 text-muted-foreground">
              <p>
                <strong className="text-foreground">Rigid panels for flat van roofs.</strong> If your Sprinter,
                Transit, or Promaster has a flat or near-flat metal roof, rigid monocrystalline panels are the
                best long-term choice. They run cooler (the 1-2 inch air gap under mounting brackets keeps cells
                15-20°C cooler than glued flexible panels), last 25+ years, and produce more watts per dollar. A
                high-roof Sprinter 144-inch model fits roughly 300W of rigid panels; the 170-inch extended fits
                400-600W.
              </p>
              <p>
                <strong className="text-foreground">Flexible panels for curved roofs and low-profile builds.</strong>
                Many van builders choose flexible panels for their ultra-low profile (no mounting brackets,
                nothing visible above the roof line) and their ability to conform to curved roof sections.
                Flexible panels are glued directly to the roof with 3M VHB tape or marine adhesive. The
                trade-off: shorter lifespan (5-10 years vs 25+), higher operating temperature, and 2-5% lower
                efficiency. For a stealth van build where profile matters more than lifespan, flexible panels are
                the right choice.
              </p>
              <p>
                <strong className="text-foreground">The hybrid approach — best of both.</strong> Many experienced
                van builders mount rigid panels on the flat sections of the roof and use a portable foldable panel
                deployed at camp. The roof panels charge while you drive and when parked; the portable panel can
                be angled directly at the sun for maximum output. A 200W roof array plus a 200W portable panel
                gives you 400W total when parked, with the portable positioned optimally.
              </p>
              <p>
                <strong className="text-foreground">Weight considerations.</strong> A 200W rigid panel weighs
                25-30 lbs; a 200W flexible panel weighs 5-8 lbs. For a Sprinter or Transit already loaded with
                a build-out, cabinet, water tanks, and gear, every pound matters. Flexible panels reduce weight
                on the roof and lower the vehicle's center of gravity. This is a real advantage for smaller vans
                and builds at the payload limit.
              </p>
            </div>
          </div>

          <div className="mt-12 max-w-3xl" id="sizing">
            <h2 className="font-heading font-bold text-2xl mb-6">How Much Solar Do You Need for Van Life?</h2>
            <div className="space-y-5 text-muted-foreground">
              <p>
                <strong className="text-foreground">Weekend van campers: 200-300W.</strong> If you use your van
                for weekend trips with basic loads (LED lights, phone charging, a small fan, occasionally a
                laptop), 200-300W of solar paired with a 100-200Ah battery handles your needs comfortably. This
                is the most common van life solar setup.
              </p>
              <p>
                <strong className="text-foreground">Part-time van life: 300-500W.</strong> If you spend weeks at
                a time in the van running a 12V compressor fridge, laptop, lights, a ventilation fan, and device
                charging, 300-500W of solar with 200-300Ah of LiFePO4 storage is the sweet spot. This covers
                your daily needs with margin for cloudy days.
              </p>
              <p>
                <strong className="text-foreground">Full-time van life: 400-800W.</strong> Full-time van dwellers
                running a fridge, laptop, ventilation, water pump, and occasional high-draw appliances
                (induction cooktop, microwave) typically need 400-800W of solar with 300-600Ah of lithium
                storage. Most full-timers also have alternator charging (via a DC-DC charger) to top up while
                driving.
              </p>
              <p>
                Use our <Link to="/solar-panel-calculator" className="text-accent-text underline underline-offset-2">solar panel calculator</Link>{" "}
                to get a precise estimate based on your specific devices and daily usage.
              </p>
            </div>
          </div>

          <div className="mt-12 max-w-3xl" id="faq">
            <h2 className="font-heading font-bold text-2xl mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                { q: "Are flexible or rigid solar panels better for van life?", a: "For flat van roofs, rigid panels are better — they last longer, run cooler, and produce more power per dollar. For curved roofs or stealth builds where a low profile matters, flexible panels are the right choice. Many van builders use rigid on flat sections and a portable panel for camp use." },
                { q: "How many watts of solar do I need for van life?", a: "Weekend van campers: 200-300W. Part-time van life: 300-500W. Full-time van life: 400-800W. The exact amount depends on your daily energy consumption — use our solar panel calculator for a precise estimate." },
                { q: "Can I run a van fridge on solar?", a: "Yes. A 12V compressor fridge (like the Dometic or Nova Kool) uses 40-80Wh per day. A 200W solar array in 5 sun hours produces 800-1000Wh — more than enough to run a fridge plus basic electronics. Pair with a 100Ah+ LiFePO4 battery for overnight operation." },
                { q: "Should I add alternator charging to my van solar setup?", a: "Yes — most full-time van lifers use a DC-DC charger (battery-to-battery charger) to charge the house battery from the van's alternator while driving. This supplements solar on cloudy days and during winter when solar production drops. A 30-60A DC-DC charger is standard for most van builds." },
                { q: "What is the best solar panel for a Sprinter van?", a: "For a high-roof Sprinter 144-inch, a 300W rigid array (two 150W or three 100W panels) or a 200-300W flexible array works well. For the 170-inch extended, you can fit 400-600W. Pair with a 30A MPPT controller and 200-300Ah of LiFePO4 storage." },
              ].map((faq, i) => (
                <div key={i} className="border border-border rounded-xl p-5">
                  <h3 className="font-heading font-semibold mb-2 text-foreground text-base">{faq.q}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link
              to="/flexible-solar-panels"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent text-foreground font-mono font-bold text-sm rounded-lg hover:bg-accent/90 min-h-[48px]"
            >
              Flexible vs Rigid Guide <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/solar-panel-calculator"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border text-sm font-medium rounded-lg hover:bg-secondary min-h-[48px]"
            >
              Calculate Van Solar Needs
            </Link>
          </div>
        </div>
        <EmailCapture source="van-life-panels" />
      </div>
    </>
  );
}